import { Badge, Caption1, makeStyles, mergeClasses, shorthands, Tab, TabList, tokens } from "@fluentui/react-components";
import { Board16Regular, CalendarAgenda16Regular } from "@fluentui/react-icons";
import { useSearchParams } from "react-router-dom";
import { SectionTitle } from "@/components/ui-typography";
import { centerFill, contentShell, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import { useMemo, useState } from "react";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { todayISO } from "@/engine/date";
import type { ExecutionStatus, ExecutionTask } from "@/engine/types";
import { ExecutionTaskDrawer } from "@/features/tasks/ExecutionTaskDrawer";
import { RecordDrawer } from "@/features/records/RecordDrawer";
import { openExecutionTask } from "@/features/tasks/links";
import { assigneeUniverse, collectExecutionTasks, type ProjectExecutionTask } from "@/features/tasks/my-tasks";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { TaskCard } from "@/features/tasks/TaskCard";
import { TaskFilterBar } from "@/features/tasks/TaskFilterBar";
import { TASK_STATUS_SCOPE_OPTIONS, taskStatusScope, withStatusScope, type TaskStatusScope } from "@/features/tasks/task-views";
import {
  applyFilter,
  DUE_LABEL,
  dueBucket,
  emptyFilter,
  matchesQuery,
  type DueBucket,
  type TaskFilter,
  type TaskQueryContext,
} from "@/features/tasks/task-query";
import { EmptyState } from "@/features/tasks/task-ui";
import { isOverdue, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER, setStatus as setStatusOp, updateChecklistItem } from "@/store/execution-task-ops";
import { UserWeeklyPanel } from "@/features/user-weekly/UserWeeklyPanel";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  lanes: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    gap: tokens.spacingHorizontalM,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalL}`,
    overflowX: "auto",
    overflowY: "hidden",
    alignItems: "stretch",
  },
  column: {
    width: "280px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.border("1px", "solid", "transparent"),
    minHeight: 0,
    overflow: "hidden",
  },
  columnHead: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
  },
  columnTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  danger: { color: tokens.colorPaletteRedForeground1 },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minHeight: 0,
    flex: 1,
    overflowX: "hidden",
    overflowY: "auto",
    paddingBottom: tokens.spacingVerticalS,
  },
  cardFit: {
    height: "auto",
    flexShrink: 0,
  },
  empty: {
    ...centerFill,
    padding: tokens.spacingHorizontalXXL,
  },
});

type LaneKey = "due" | "project" | "status" | "priority" | "assignee";

const LANE_OPTIONS: Array<{ value: LaneKey; label: string }> = [
  { value: "due", label: "마감" },
  { value: "project", label: "플랜" },
  { value: "status", label: "상태" },
  { value: "priority", label: "우선순위" },
  { value: "assignee", label: "담당자" },
];

const DUE_ORDER: DueBucket[] = ["overdue", "today", "week", "later", "none"];

function initialFilter(): TaskFilter {
  const f = emptyFilter();
  const a = useCatalogStore.getState().myTasksAssignee;
  if (a === "__unassigned") f.assignees = [""];
  else if (a) f.assignees = [a];
  return f;
}

/** Every execution task across projects, as a board. Open-project edits win over the stored copy. */
export function MyTasksPage() {
  const styles = useStyles();
  // 내 작업은 여러 플랜을 가로지른다. Task는 플랜 안에서 만든다.
  usePageAction(null);
  const allowCreate = canWorkspace(useWorkspaceRole(), "plan.create");
  const catalog = useCatalogStore((s) => s.projects);
  const resources = useCatalogStore((s) => s.resources);
  const profile = useCatalogStore((s) => s.profile);
  const [params, setParams] = useSearchParams();
  const workTab = params.get("tab") === "weekly" ? "weekly" : "board";
  const activeId = useProjectStore((s) => s.activeId);
  const openProject = useProjectStore((s) => s.project);
  const openSchedule = useProjectStore((s) => s.derived.schedule);
  const [filter, setFilter] = useState<TaskFilter>(initialFilter);
  const [lane, setLane] = useState<LaneKey>("due");
  const today = todayISO();
  const emptyCtx = useMemo<TaskQueryContext>(
    () => ({ project: { tasks: [], planner: { buckets: [], labels: [], customFields: [] } }, schedule: null, today }),
    [today],
  );

  const all = useMemo(() => collectExecutionTasks(catalog, activeId ? openProject : null), [catalog, activeId, openProject]);
  const names = useMemo(() => assigneeUniverse(all), [all]);
  const ctxFor = useMemo(() => {
    const cache = new Map<string, TaskQueryContext>();
    return (projectId: string): TaskQueryContext => {
      let c = cache.get(projectId);
      if (!c) {
        const p = projectId === activeId ? openProject : catalog[projectId];
        c = { project: p ?? { tasks: [], planner: { buckets: [], labels: [], customFields: [] } }, schedule: projectId === activeId ? openSchedule : null, today };
        cache.set(projectId, c);
      }
      return c;
    };
  }, [activeId, openProject, openSchedule, catalog, today]);

  const items = useMemo(() => {
    return all.filter((x) => {
      const ctx = ctxFor(x.projectId);
      const withoutQuery = filter.query.trim() ? { ...filter, query: "" } : filter;
      if (!applyFilter([x.task], withoutQuery, ctx).length) return false;
      if (!filter.query.trim()) return true;
      return matchesQuery(x.task, filter.query, ctx) || x.projectName.toLowerCase().includes(filter.query.toLowerCase());
    });
  }, [all, filter, ctxFor]);

  const lanes = useMemo(() => groupMyTasks(items, lane, today), [items, lane, today]);
  const description = useMemo(() => {
    const plans = new Set(all.map((item) => item.projectId)).size;
    let open = 0;
    let overdue = 0;
    for (const { task } of all) {
      if (task.status === "done") continue;
      open += 1;
      if (isOverdue(task, today)) overdue += 1;
    }
    return [plans > 0 ? `${plans}개 플랜` : "", `${all.length}건`, `미완료 ${open}`, overdue > 0 ? `기한 지남 ${overdue}` : ""].filter(Boolean).join(" · ");
  }, [all, today]);
  const statusScope = taskStatusScope(filter);
  const setStatusScope = (scope: TaskStatusScope) => setFilter(withStatusScope(filter, scope));
  const selectWorkTab = (next: "board" | "weekly") => {
    const updated = new URLSearchParams(params);
    if (next === "weekly") updated.set("tab", "weekly");
    else updated.delete("tab");
    setParams(updated, { replace: true });
  };
  const workTabs = (
    <TabList
      size="small"
      selectedValue={workTab}
      aria-label="내 작업"
      onTabSelect={(_, data) => {
        if (data.value === "board" || data.value === "weekly") selectWorkTab(data.value);
      }}
    >
      <Tab value="board" icon={<Board16Regular />}>작업 보드</Tab>
      <Tab value="weekly" icon={<CalendarAgenda16Regular />}>주간 보고</Tab>
    </TabList>
  );
  const selfResourceId = Object.values(resources).find((resource) => resource.displayName === profile.displayName)?.id;

  const setStatus = (projectId: string, id: string, status: ExecutionStatus | undefined) => {
    if (projectId === activeId) useProjectStore.getState().setExecutionStatus(id, status);
    else useCatalogStore.getState().mutateProject(projectId, (p) => ({ ...p, executionTasks: setStatusOp(p.executionTasks, id, status, profile.displayName).list }));
  };

  const patchTask = (projectId: string, id: string, patch: Partial<ExecutionTask>) => {
    const now = new Date().toISOString();
    if (projectId === activeId) useProjectStore.getState().updateExecutionTasks([id], patch, "Task 변경");
    else {
      useCatalogStore.getState().mutateProject(projectId, (p) => ({
        ...p,
        executionTasks: p.executionTasks.map((t) => (t.id === id ? { ...t, ...patch, updatedAt: now } : t)),
      }));
    }
  };

  const toggleChecklist = (projectId: string, taskId: string, itemId: string, done: boolean) => {
    if (projectId === activeId) {
      useProjectStore.getState().updateChecklistItem(taskId, itemId, { done });
    } else {
      useCatalogStore.getState().mutateProject(projectId, (p) => ({
        ...p,
        executionTasks: p.executionTasks.map((t) => (t.id === taskId ? updateChecklistItem(t, itemId, { done }) : t)),
      }));
    }
  };

  if (workTab === "weekly") {
    return (
      <>
        <UserWeeklyPanel
          title="내 작업"
          actor={{ name: profile.displayName, ...(selfResourceId ? { resourceId: selfResourceId } : {}) }}
          projects={catalog}
          live={activeId && openProject.id === activeId ? openProject : null}
          resources={resources}
          sections={workTabs}
        />
        <ExecutionTaskDrawer />
        <RecordDrawer />
      </>
    );
  }

  return (
    <div className={styles.page}>
      <TaskFilterBar
        title="내 작업"
        description={description}
        sections={workTabs}
        filter={filter}
        onChange={setFilter}
        ctx={emptyCtx}
        assignees={names}
        noScope
        statusInScope
        scope={
          <SegmentedControl
            ariaLabel="상태"
            value={statusScope}
            onChange={setStatusScope}
            options={TASK_STATUS_SCOPE_OPTIONS}
          />
        }
      >
        <FuiFilterSelect label="열" value={lane} options={LANE_OPTIONS} onValueChange={(v) => setLane(v as LaneKey)} />
      </TaskFilterBar>

      {Object.keys(catalog).length === 0 ? (
        <div className={styles.empty}>
          <EmptyState title="플랜이 없습니다" hint="플랜을 만들면 그 안의 Task가 여기에 모입니다." action={allowCreate ? <FuiButton appearance="primary" icon={<Board16Regular />} onClick={() => useProjectStore.getState().openDialog("create-plan")}>플랜 추가</FuiButton> : undefined} />
        </div>
      ) : all.length === 0 ? (
        <div className={styles.empty}>
          <EmptyState title="보드가 비어 있습니다" hint="플랜 안에서 Task를 만들면 마감·플랜·상태 열로 모입니다." />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <EmptyState
            title="필터와 일치하는 Task가 없습니다"
            hint="필터를 지우거나 다른 조건을 선택하세요."
            action={<FuiButton onClick={() => setFilter(emptyFilter())}>필터 지우기</FuiButton>}
          />
        </div>
      ) : (
        <div className={styles.lanes}>
          {lanes.map((sec) => (
            <section key={sec.key} className={styles.column} aria-label={`${sec.label} ${sec.items.length}개`}>
              <header className={styles.columnHead}>
                <SectionTitle className={mergeClasses(styles.columnTitle, sec.danger && styles.danger)} title={sec.label}>
                  {sec.label}
                </SectionTitle>
                <Badge appearance="tint" color="subtle" size="small">
                  {sec.items.length}
                </Badge>
              </header>
              <div className={styles.list}>
                {sec.items.map(({ projectId, projectName, task }) => (
                  <TaskCard
                    key={`${projectId}:${task.id}`}
                    className={styles.cardFit}
                    task={task}
                    ctx={ctxFor(projectId)}
                    projectId={projectId}
                    projectName={lane === "project" ? undefined : projectName}
                    onOpen={() => openExecutionTask(projectId, task.id)}
                    onStatus={(id, status) => setStatus(projectId, id, status)}
                    onPriority={(id, priority) => patchTask(projectId, id, { priority })}
                    onAssignees={(id, assignees) => patchTask(projectId, id, { assignees })}
                    assigneeOptions={names}
                    onToggleChecklist={(taskId, itemId, done) => toggleChecklist(projectId, taskId, itemId, done)}
                  />
                ))}
                {sec.items.length === 0 && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground4, padding: tokens.spacingHorizontalS }}>해당 없음</Caption1>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
      <ExecutionTaskDrawer />
      <RecordDrawer />
    </div>
  );
}

function groupMyTasks(items: ProjectExecutionTask[], lane: LaneKey, today: string) {
  if (lane === "due") {
    return DUE_ORDER.map((d) => ({
      key: d,
      label: DUE_LABEL[d],
      items: items.filter((x) => dueBucket(x.task, today) === d),
      danger: d === "overdue",
    }));
  }
  if (lane === "project") {
    const ids = [...new Set(items.map((x) => x.projectId))];
    return ids.map((pid) => ({
      key: pid,
      label: items.find((x) => x.projectId === pid)?.projectName || "이름 없는 플랜",
      items: items.filter((x) => x.projectId === pid),
      danger: false,
    }));
  }
  if (lane === "status") {
    return [
      { key: "", label: "상태 미정", items: items.filter((x) => !x.task.status), danger: false },
      ...STATUS_ORDER.map((s) => ({
        key: s,
        label: STATUS_LABEL[s],
        items: items.filter((x) => x.task.status === s),
        danger: false,
      })),
    ];
  }
  if (lane === "priority") {
    return [
      ...PRIORITY_ORDER.map((p) => ({
        key: p,
        label: PRIORITY_LABEL[p],
        items: items.filter((x) => x.task.priority === p),
        danger: false,
      })),
      { key: "", label: "우선순위 미정", items: items.filter((x) => !x.task.priority), danger: false },
    ];
  }
  const names = [...new Set(items.flatMap((x) => x.task.assignees))].sort((a, b) => a.localeCompare(b, "ko"));
  return [
    ...names.map((n) => ({
      key: n,
      label: n,
      items: items.filter((x) => x.task.assignees.includes(n)),
      danger: false,
    })),
    { key: "", label: "담당 미정", items: items.filter((x) => x.task.assignees.length === 0), danger: false },
  ];
}
